import { AxiosError } from 'axios';
// Bare i18next singleton, not '../i18n': the init module calls `.use(initReactI18next)` at
// load, which crashes test files that mock react-i18next without that export. main.tsx
// imports '../i18n' for side-effect init before App, so this singleton is configured
// app-wide at runtime and the two references are the same object.
import i18n from 'i18next';
import { I18N_KEY_PATTERN } from '../../types/i18nKeyPattern';

/**
 * The one reader for everything the API refuses with (PBI #1739 Faz 2, AB#1767).
 *
 * <b>Why one.</b> Two shared extractors used to split this job and neither was a superset of
 * the other: `utils/extractApiError` understood `errorKey`/`error`/`messageKey` and the
 * per-status generic fallback but nothing about per-field errors; `lib/forms/mapBackendErrors`
 * understood ValidationProblemDetails' `errors` but was blind to the ~132 `errorKey` sites and
 * rendered RFC 7807's English `title`. Which of the two a screen happened to import decided
 * whether the user saw the real reason, and that is not a property any reviewer can see from
 * one side of the wire. Both now delegate here and hold no parsing of their own.
 */

/** True when the whole string is shaped like an i18n key (never free text or an exception message). */
export function isI18nKey(value: string): boolean {
  return I18N_KEY_PATTERN.test(value);
}

/** `detail` value emitted by PermissionAuthorizationExtensions on every denied request. */
const AUTHORIZATION_MISSING_KEY = 'errors.authorization.permission.missing';

/**
 * Key rendered instead of a translation that still holds an unresolved `{{placeholder}}` after
 * interpolation, i.e. the key needed params that did not arrive. No status is available at this
 * point (`translateApiMessage` is also called standalone), so this cannot be the per-status map.
 */
const MISSING_INTERPOLATION_FALLBACK_KEY = 'errors.generic.unexpected';

/** Detects an i18next placeholder (`{{name}}`) surviving translation. */
const RAW_PLACEHOLDER_PATTERN = /\{\{\s*[\w.]+\s*\}\}/;

/** True when a translated string still carries an unresolved `{{placeholder}}`, i.e. a param never arrived. */
export function hasUnresolvedPlaceholder(translated: string): boolean {
  return RAW_PLACEHOLDER_PATTERN.test(translated);
}

/**
 * Status to generic i18n key, used only when the response carried no reason at all and the
 * caller named no fallback of its own. The server fills this in itself for bodiless refusals
 * (`AddProblemDetails` + `UseStatusCodePages`), so this map is the second line of defence: it
 * covers responses that write their own body and therefore skip that path, chiefly the 5xx from
 * `ExceptionClassifierMiddleware` whose only human-facing field is an English `title`.
 * Names must stay in step with `GenericErrorMessageKeys` on the server.
 */
const GENERIC_KEY_BY_STATUS: Record<number, string> = {
  400: 'errors.generic.badRequest',
  401: 'errors.generic.unauthorized',
  403: 'errors.generic.forbidden',
  404: 'errors.generic.notFound',
  405: 'errors.generic.methodNotAllowed',
  409: 'errors.generic.conflict',
  410: 'errors.generic.gone',
  413: 'errors.generic.payloadTooLarge',
  415: 'errors.generic.unsupportedMediaType',
  422: 'errors.generic.unprocessable',
  429: 'errors.generic.tooManyRequests',
};

const UNEXPECTED_KEY = 'errors.generic.unexpected';

/**
 * `i18n.t` with the one guarantee the rest of this module depends on: a string comes back.
 *
 * i18next answers `undefined` when it has not been initialised — which is not a theoretical
 * state. Every screen reaches this reader, and a chunk of the component suite mocks
 * `lib/i18n` (the init module) without touching the `i18next` singleton this file talks to,
 * so `t()` there is the bare, un-inited one. Without this guard that `undefined` travels all
 * the way to `toast.error(undefined)` and the user is shown an empty notification instead of
 * the reason their request was refused. Echoing the key back is what i18next itself does for
 * a key it cannot resolve, and it keeps the `t(key) === key` "missing" idiom below working.
 */
function t(key: string, params?: Record<string, unknown>): string {
  const translated = i18n.t(key, params);
  return typeof translated === 'string' ? translated : key;
}

function genericKeyFor(status: number | undefined): string {
  if (status === undefined) return UNEXPECTED_KEY;
  return (
    GENERIC_KEY_BY_STATUS[status] ?? (status >= 500 ? 'errors.generic.serverError' : UNEXPECTED_KEY)
  );
}

/**
 * Resolves a backend reason through the active locale, interpolating `params` when the key
 * carries placeholders. Key-shaped values are translated; anything else (or an unknown key,
 * which i18next echoes back unchanged) falls back to the original string.
 *
 * A translation that still contains `{{placeholder}}` after interpolation means the key needed
 * params this call did not receive. Rendering the raw placeholder is worse than a generic
 * sentence, so that falls back to `MISSING_INTERPOLATION_FALLBACK_KEY` instead (AB#1750).
 */
export function translateApiMessage(message: string, params?: Record<string, unknown>): string {
  if (!isI18nKey(message)) return message;
  const translated = t(message, params);
  if (translated === message) return message;
  if (RAW_PLACEHOLDER_PATTERN.test(translated)) return t(MISSING_INTERPOLATION_FALLBACK_KEY);
  return translated;
}

/**
 * The strict sibling of `translateApiMessage` for surfaces that must not show anything they
 * cannot vouch for: the translation of a key the locale knows and fully interpolates, otherwise
 * null so the caller picks its own generic sentence. `translate` lets a component pass its hook
 * `t`; it defaults to the shared singleton.
 */
export function translateKnownKey(
  key: string,
  params?: Record<string, unknown>,
  translate: (key: string, params?: Record<string, unknown>) => string = t,
): string | null {
  if (!isI18nKey(key)) return null;
  const translated = translate(key, params);
  if (typeof translated !== 'string' || translated === key) return null;
  return RAW_PLACEHOLDER_PATTERN.test(translated) ? null : translated;
}

/**
 * "Products.Write" to the translated label "Ürünler (Yazma)", or null when the key has no label
 * yet. Returning null rather than the raw key is the leak guard: a permission key is an internal
 * identifier and must never reach the screen.
 */
function translatedPermissionLabel(permissionKey: string): string | null {
  const joined = permissionKey.replace(/\./g, '');
  const camel = joined.charAt(0).toLowerCase() + joined.slice(1);
  const path = `settings.security.permissions.key.${camel}`;
  const label = t(path);
  return label === path ? null : label;
}

const toCamelCase = (s: string) => (s.length === 0 ? s : s.charAt(0).toLowerCase() + s.slice(1));

/**
 * Every field the API has ever put a reason in. `message` is the only one Faz 1 (AB#1766)
 * leaves for new code; the rest are read because un-migrated endpoints still emit them and a
 * reader that stops understanding one silently blanks every screen behind it.
 */
interface BackendErrorBody {
  message?: string;
  error?: string;
  errorKey?: string;
  messageKey?: string;
  detail?: string;
  title?: string;
  permission?: string;
  errors?: Record<string, string[]>;
  /**
   * i18next interpolation values matched by name to the `{{placeholder}}` tokens in the
   * translated string. `Refuse.Build` emits it beside `message`; the older hand-rolled
   * envelopes emit it beside `errorKey`.
   */
  params?: Record<string, unknown>;
}

/** The full reading of a refusal. Every field is ready to render as-is. */
export interface ApiError {
  /** Translated and safe to put on screen. Never a raw key, never an English RFC 7807 title. */
  message: string;
  /** The raw backend value behind `message`, for the rare caller that branches on a key. */
  messageKey?: string;
  status?: number;
  /**
   * Per-field reasons from ValidationProblemDetails, camelCased to match form field names and
   * already translated. Absent when the response carried no `errors` dictionary.
   */
  fieldErrors?: Record<string, string>;
  /**
   * True when the request was refused for lack of a permission (or authentication). Callers use
   * this to decide whether a retry affordance is honest: a denied write fails identically every
   * time, so offering "try again" for one is actively misleading.
   */
  isAuthorization: boolean;
}

function parseFieldErrors(data: BackendErrorBody | undefined): Record<string, string> | undefined {
  if (!data?.errors || Object.keys(data.errors).length === 0) return undefined;
  const record: Record<string, string> = {};
  for (const [field, messages] of Object.entries(data.errors)) {
    const first = messages?.[0];
    if (!first) continue;
    record[toCamelCase(field)] = translateApiMessage(first);
  }
  return Object.keys(record).length > 0 ? record : undefined;
}

/** A raw, untranslated reason plus the interpolation values it needs, if any. */
interface RawReason {
  message: string;
  params?: Record<string, unknown>;
}

/**
 * Picks the raw reason out of a response body, honouring every envelope the backend emits.
 * Returns null when the body carries no reason this reader understands.
 *
 * `params` rides with whichever field carried the reason. Attaching it only to `errorKey` (as
 * one of the two old readers did) silently broke every `Refuse.X(key, args)` site: those put
 * the key in `message` and the values in `params`, so the values were dropped, the translation
 * kept its `{{placeholder}}` tokens and the placeholder guard replaced the whole sentence with
 * "an unexpected error occurred".
 *
 * `title` is deliberately absent. It is an RFC 7807 machine-facing field and every value this
 * API puts there is an untranslated English literal ("Unexpected error", "Forbidden");
 * rendering it is how a 500 came to greet Turkish users in English.
 */
function rawReasonFrom(data: BackendErrorBody | undefined): RawReason | null {
  if (!data) return null;
  const params = data.params;
  if (data.message) return { message: data.message, params };
  if (data.error) return { message: data.error, params };
  if (data.errorKey) return { message: data.errorKey, params };
  if (data.messageKey) return { message: data.messageKey, params };
  if (data.errors) {
    for (const messages of Object.values(data.errors)) {
      const first = messages?.[0];
      if (first) return { message: first, params };
    }
  }
  if (data.detail) return { message: data.detail, params };
  return null;
}

/**
 * Normalizes an unknown catch value into a structured, ready-to-render error.
 *
 * `fallbackKey` is the reason to show when the response carried none of its own — the caller's
 * contextual sentence ("Kaydedilemedi") in place of the per-status generic one. Callers used to
 * express this as `getErrorMessage(e) || t('...')`, which never fired: the left side is always a
 * non-empty string, so the `||` branch was dead and the contextual key was never reachable.
 */
export function readApiError(e: unknown, fallbackKey?: string): ApiError {
  if (e instanceof AxiosError) {
    // No response at all is a transport failure, not a refusal — there is no envelope to read
    // and no status to map. The axios message is kept as-is; for calls made through
    // `lib/api.ts` the interceptor has already replaced it with a translated sentence.
    if (!e.response) {
      return { message: e.message, isAuthorization: false };
    }

    const status = e.response.status;
    const data = e.response.data as BackendErrorBody | undefined;
    const isAuthorization =
      status === 401 || status === 403 || data?.detail === AUTHORIZATION_MISSING_KEY;
    const fieldErrors = parseFieldErrors(data);

    // A permission denial carries which permission is missing. Render its translated label,
    // never the raw key (an internal identifier) and never the i18n key itself.
    if (data?.detail === AUTHORIZATION_MISSING_KEY) {
      const label = data.permission ? translatedPermissionLabel(data.permission) : null;
      return {
        message: label
          ? t('errors.authorization.permission.missingNamed', { permission: label })
          : t(AUTHORIZATION_MISSING_KEY),
        messageKey: AUTHORIZATION_MISSING_KEY,
        status,
        fieldErrors,
        isAuthorization,
      };
    }

    const raw = rawReasonFrom(data);
    if (raw) {
      return {
        message: translateApiMessage(raw.message, raw.params),
        messageKey: raw.message,
        status,
        fieldErrors,
        isAuthorization,
      };
    }

    // Nothing usable in the body. Axios's own `e.message` here is "Request failed with status
    // code 400", which tells the user nothing and is not translated.
    const generic = fallbackKey ?? genericKeyFor(status);
    return {
      message: translateApiMessage(generic),
      messageKey: generic,
      status,
      fieldErrors,
      isAuthorization,
    };
  }

  // A rejected promise that is not an AxiosError carries no envelope. `Error.message` is kept
  // because `lib/api.ts` delivers its translated network/session sentences that way.
  if (e instanceof Error) {
    return { message: e.message, isAuthorization: false };
  }

  // Anything else is a programming fault, not a refusal. `String(e)` here is how raw values
  // ("[object Object]", a thrown string) used to reach the screen.
  const key = fallbackKey ?? UNEXPECTED_KEY;
  return { message: translateApiMessage(key), messageKey: key, isAuthorization: false };
}

/**
 * The strict reading of a refusal for a surface that keeps its own generic sentence: the
 * translated reason when the body carries one the locale knows and fully interpolates,
 * otherwise null. Unlike `readApiError` it never falls back to the raw key or a per-status
 * generic, so the caller's contextual fallback stays reachable.
 */
export function readKnownApiReason(e: unknown): string | null {
  const raw = rawReasonFrom(readApiErrorBody<BackendErrorBody>(e));
  return raw ? translateKnownKey(raw.message, raw.params) : null;
}

/**
 * The typed body of a refused request, for callers that need a field `readApiError` does not
 * surface (a conflict list, a `canManualMap` flag, an extra `unresolved` array).
 *
 * Returns undefined unless `e` carries `response.data` that is a plain object. A string, a
 * Blob or an array is never handed back as if it were an object: reading a field off a Blob
 * yields undefined and the caller would silently take its "no extra data" branch. The check
 * is structural rather than `instanceof AxiosError` because several callers (and their tests)
 * receive duck-typed rejections. The type parameter is the caller's claim about the wire
 * shape, so every field of `T` is optional.
 */
export function readApiErrorBody<T extends object>(e: unknown): Partial<T> | undefined {
  if (e === null || typeof e !== 'object' || !('response' in e)) return undefined;
  const response = (e as { response?: { data?: unknown } }).response;
  const data = response?.data;
  if (data === null || typeof data !== 'object' || Array.isArray(data) || data instanceof Blob) {
    return undefined;
  }
  return data as Partial<T>;
}

/**
 * `readApiError` for requests issued with `responseType: 'blob'`.
 *
 * <b>Why this is not optional.</b> axios honours `responseType` on the error path too, so a
 * refusal to a blob request arrives with `response.data` as a `Blob` — every field the reader
 * above looks for (`message`, `errorKey`, `detail`, ...) is `undefined` on a Blob, `rawReasonFrom`
 * returns null, and the reason the server took the trouble to send is discarded in favour of the
 * per-status generic. The body is on the wire and still invisible: adding a reason server-side
 * without this step changes nothing a user can see.
 *
 * Only JSON-ish blobs are unpacked. A refusal that genuinely streamed bytes (or an opaque
 * `application/octet-stream` error page) falls through to the generic path rather than having its
 * raw contents rendered as a sentence — the failure mode the terminal-job followup warns about.
 */
export async function readApiErrorAsync(e: unknown, fallbackKey?: string): Promise<ApiError> {
  if (e instanceof AxiosError && e.response?.data instanceof Blob) {
    const blob = e.response.data;
    // Some servers omit the type on an error blob; attempt the parse when it is absent too,
    // and let the try/catch below decide. Guard the size so a large streamed body is not read.
    const looksJson = !blob.type || blob.type.includes('json') || blob.type.includes('text');
    if (looksJson && blob.size > 0 && blob.size <= 64 * 1024) {
      try {
        const parsed: unknown = JSON.parse(await blob.text());
        if (parsed && typeof parsed === 'object') {
          e.response.data = parsed;
        }
      } catch {
        // Not JSON after all — leave the Blob in place; readApiError falls back to the
        // per-status generic, which is the correct outcome for an unreadable body.
      }
    }
  }
  return readApiError(e, fallbackKey);
}
